import type {
  CartItem, CartResponse, CartSeatDetail, CartShippingAddress,
} from "@/features/cart/types/cart.type";
import type {
  DealCart, DealCartDeliveryMode, DealCartItem, DealCartShippingAddress,
} from "@/features/deals/types/dealCart.type";
import type { RentalBooking } from "@/features/rentals/types/rentals.type";

/**
 * Cart type discriminator. Rentals share the same `/boutique/panier` shell
 * as boutique/deals via a third tab and the same `UnifiedCartItemCard` /
 * `CartSummary` layout. Each booking is still paid individually (one
 * Paybox session per booking), but the cart UI is iso.
 */
export type CartType = "boutique" | "deals" | "rentals";

/**
 * Unified shipping address that works for both cart types
 */
export type UnifiedShippingAddress = {
  first_name?: string
  last_name?: string
  name?: string
  address_line1: string
  address_line2?: string | null
  zip_code: string
  city: string
};

/**
 * Base unified cart item with common fields.
 *
 * Convention de montants, à respecter dans tous les transformers : seul
 * `total_amount` est un total de ligne. `price_amount`,
 * `original_price_amount` et `discount_amount` sont **unitaires**, à charge
 * de l'affichage de multiplier par `quantity` (BNF-398).
 */
type UnifiedCartItemBase = {
  id: string
  cartType: CartType
  quantity: number
  price_amount: number
  total_amount: number
  name: string
  image_url: string | null
  subtitle?: string
  discount_amount: number
  original_price_amount: number
  seat_details?: CartSeatDetail[]
  seat_selection_required?: boolean
};

/**
 * Boutique cart item in unified format
 */
export type UnifiedBoutiqueCartItem = UnifiedCartItemBase & {
  cartType: "boutique"
  originalItem: CartItem
  item_type: "article" | "offer" | "rate" | "benefits"
  minimum_quantity?: number
  multiple_quantity?: number
  maximum_quantity: number | null
  type_billet?: string
};

/**
 * Deals cart item in unified format
 */
export type UnifiedDealsCartItem = UnifiedCartItemBase & {
  cartType: "deals"
  originalItem: DealCartItem
  reference_id: string
  family_id: string
  family_name: string
  ref?: string
};

/**
 * Rentals cart item in unified format. Maps a `RentalBooking` (status =
 * "cart") onto the same shape as boutique/deals items so the
 * `UnifiedCartItemCard` and `CartSummary` can render it without
 * branching on cartType for layout purposes.
 *
 * Notable simplifications vs. boutique/deals:
 * - quantity is always 1 (the API quantity sits inside the booking),
 *   the card hides the +/- controls
 * - no discount aggregation here — subsidies on rentals are configured
 *   per-booking server-side, not toggled cart-side
 * - `ref` carries the booking's business reference (e.g. `CAT-700-…`)
 *   so the recap can show it under "Réf."
 */
export type UnifiedRentalsCartItem = UnifiedCartItemBase & {
  cartType: "rentals"
  originalItem: RentalBooking
  ref?: string
  /** Caution snapshotée (€) en plus du loyer — affichée comme ligne
   * séparée dans la carte panier et le récap, restituée au retour. */
  deposit_amount: number
};

/**
 * Unified cart item - discriminated union
 */
export type UnifiedCartItem =
  | UnifiedBoutiqueCartItem
  | UnifiedDealsCartItem
  | UnifiedRentalsCartItem;

/**
 * Unified cart response that works for both cart types
 */
export type UnifiedCart = {
  id: string
  cartType: CartType
  items: UnifiedCartItem[]
  total_items: number
  total_amount: number
  errors?: string[]
  shipping_address: UnifiedShippingAddress | null
  // Boutique-specific fields (optional)
  ttl?: number | null
  reserved_until?: string | null
  reservation_expires_at?: Date | null
  // Deals-specific fields (optional)
  ref?: string
  status?: string
  status_name?: string
  delivery_mode?: DealCartDeliveryMode
};

/**
 * Original cart types for reference in mutations
 */
export type OriginalBoutiqueCart = CartResponse;
export type OriginalDealsCart = DealCart;

/**
 * Type guard for boutique cart items
 */
export const isBoutiqueCartItem = (
  item: UnifiedCartItem,
): item is UnifiedBoutiqueCartItem => item.cartType === "boutique";

/**
 * Type guard for deals cart items
 */
export const isDealsCartItem = (
  item: UnifiedCartItem,
): item is UnifiedDealsCartItem => item.cartType === "deals";

/**
 * Type guard for rentals cart items
 */
export const isRentalsCartItem = (
  item: UnifiedCartItem,
): item is UnifiedRentalsCartItem => item.cartType === "rentals";

/**
 * Original shipping address types for type mapping
 */
export type OriginalBoutiqueShippingAddress = CartShippingAddress;
export type OriginalDealsShippingAddress = DealCartShippingAddress;
